// Localisable UI strings for the weather components. Every field is optional;
// WEATHER_DEFAULT_LABELS keeps the original English copy, so existing usages
// that pass no labels render exactly as before.

export interface WeatherLabels {
  // WeatherDashboard
  unavailable?: string;
  feelsLike?: string; // {temp} placeholder
  rowWind?: string;
  rowHumidity?: string;
  rowVisibility?: string;
  rowPrecipitation?: string;
  rowCloudCover?: string;
  rowSnowDepth?: string;
  rowPressure?: string;
  source?: string;
  cloudDescriptions?: Record<number, string>;
  updatedJustNow?: string;
  updatedMinute?: string;
  updatedMinutes?: string; // {count} placeholder
  updatedHour?: string;
  updatedHours?: string; // {count} placeholder
  updatedDay?: string;
  updatedDays?: string; // {count} placeholder
  // ForecastCards
  forecastUnavailable?: string;
  // WindyMap
  mapTitle?: string;
  mapTabs?: { temp?: string; wind?: string; snow?: string; rain?: string; clouds?: string };
  mapIframeTitle?: string; // {overlay} placeholder
  // HistoricalAverages
  historyTitle?: string;
  historySubtitle?: string;
  historyHeaders?: { month?: string; avgTemp?: string; snowDepth?: string; daylight?: string; bestFor?: string };
  historyMonths?: Record<string, string>;
  historyBestFor?: Record<string, string>;
  historyDaylight?: Record<string, string>;
  // SnowDepthGauge / SnowDepthTable
  gaugeNoData?: string;
  tableBaseDepth?: string;
  tableSummitDepth?: string;
  tableSlopesOpen?: string;
  tableLiftsOpen?: string;
  tableAverage?: string;
  tableNoData?: string;
  // Snow ratings (Poor/Fair/Good/Excellent/Epic)
  ratings?: { Poor?: string; Fair?: string; Good?: string; Excellent?: string; Epic?: string };
  // WeatherWidget
  widgetUnavailable?: string;
  widgetNoSnow?: string;
  widgetCloud?: { clear?: string; partly?: string; mostly?: string; overcast?: string };
  // WeatherTopBar
  topbarLoading?: string;
  topbarUnavailable?: string;
  topbarSnow?: string;
  topbarSnowIndex?: string;
  topbarDismiss?: string;
}

export const WEATHER_DEFAULT_LABELS = {
  unavailable: 'Weather data is currently unavailable. Please try again later.',
  feelsLike: 'Feels like {temp}°C',
  rowWind: 'Wind',
  rowHumidity: 'Humidity',
  rowVisibility: 'Visibility',
  rowPrecipitation: 'Precipitation (1h)',
  rowCloudCover: 'Cloud cover',
  rowSnowDepth: 'Snow depth',
  rowPressure: 'Pressure',
  source: 'Source: Finnish Meteorological Institute, CC BY 4.0',
  updatedJustNow: 'Updated just now',
  updatedMinute: 'Updated 1 minute ago',
  updatedMinutes: 'Updated {count} minutes ago',
  updatedHour: 'Updated 1 hour ago',
  updatedHours: 'Updated {count} hours ago',
  updatedDay: 'Updated 1 day ago',
  updatedDays: 'Updated {count} days ago',
  forecastUnavailable: 'Forecast unavailable',
  mapTitle: 'Interactive Weather Map',
  mapTabs: {
    temp: 'Temperature',
    wind: 'Wind',
    snow: 'Snow',
    rain: 'Precipitation',
    clouds: 'Clouds',
  },
  mapIframeTitle: 'Windy weather map - {overlay} overlay',
  historyTitle: 'Historical Monthly Averages',
  historySubtitle: 'Climate Data',
  historyHeaders: {
    month: 'Month',
    avgTemp: 'Avg Temp',
    snowDepth: 'Snow Depth',
    daylight: 'Daylight Hours',
    bestFor: 'Best For',
  },
  gaugeNoData: 'No data',
  tableBaseDepth: 'Base Depth',
  tableSummitDepth: 'Summit Depth',
  tableSlopesOpen: 'Slopes Open',
  tableLiftsOpen: 'Lifts Open',
  tableAverage: 'Kettura Average',
  tableNoData: 'No data',
  ratings: {
    Poor: 'Poor',
    Fair: 'Fair',
    Good: 'Good',
    Excellent: 'Excellent',
    Epic: 'Epic',
  },
  widgetUnavailable: 'Weather unavailable',
  widgetNoSnow: 'No snow',
  widgetCloud: {
    clear: 'Clear',
    partly: 'Partly cloudy',
    mostly: 'Mostly cloudy',
    overcast: 'Overcast',
  },
  topbarLoading: 'Loading conditions...',
  topbarUnavailable: 'Conditions unavailable',
  topbarSnow: 'Snow:',
  topbarSnowIndex: 'Snow Index:',
  topbarDismiss: 'Dismiss weather bar',
} as const;

/** Merge provided labels over the English defaults (nested groups included). */
export function resolveWeatherLabels(labels?: WeatherLabels) {
  const d = WEATHER_DEFAULT_LABELS;
  return {
    ...d,
    ...labels,
    mapTabs: { ...d.mapTabs, ...labels?.mapTabs },
    historyHeaders: { ...d.historyHeaders, ...labels?.historyHeaders },
    ratings: { ...d.ratings, ...labels?.ratings },
    widgetCloud: { ...d.widgetCloud, ...labels?.widgetCloud },
    cloudDescriptions: labels?.cloudDescriptions ?? {},
    historyMonths: labels?.historyMonths ?? {},
    historyBestFor: labels?.historyBestFor ?? {},
    historyDaylight: labels?.historyDaylight ?? {},
  };
}

export type ResolvedWeatherLabels = ReturnType<typeof resolveWeatherLabels>;
